import apiClient from "@/api/apiClient";
import {
  createPaginatedSupabaseQuery,
  spawnCreateApiFunction,
  spawnDeleteApiFunction,
  spawnGetApiFunction,
  spawnUpdateApiFunction,
  type ApiResourceConfig,
} from "@/utils/api";
import { type QueryFilter } from "@/utils/filter";
import { type QueryPaginationFilters } from "@/utils/pagination";

const table = "maintenance_strategies";
const query = `
                *,
                maintenance_strategies_to_devices(device_id, device:devices(id, device_tags(id))),
                maintenance_strategy_intervals(*),
                maintenance_strategy_triggers(*)
              `;
const queryByDevice = `
                        *,
                        maintenance_strategies_to_devices!inner(device_id, device:devices(id, device_tags(id))),
                        maintenance_strategy_intervals(*),
                        maintenance_strategy_triggers(*)
                      `;

export const apiConfigMaintenanceStrategies: ApiResourceConfig<typeof table, typeof query> = {
  table,
  query,
  searchables: ["title", "description"],
  sortables: ["title", "created_at", "updated_at", "is_active"],
};

export const getMaintenanceStrategy = spawnGetApiFunction({ table, query });
export const createMaintenanceStrategy = spawnCreateApiFunction({ table, query });
export const updateMaintenanceStrategy = spawnUpdateApiFunction({ table, query });
export const deleteMaintenanceStrategy = spawnDeleteApiFunction({ table });

export const getMaintenanceStrategiesByDevice = async (device_id: string) => {
  const { data, error } = await apiClient
    .from(table)
    .select(queryByDevice)
    .eq("maintenance_strategies_to_devices.device_id", device_id);
  if (error) throw error;
  return data;
};

export async function getPaginatedMaintenanceStrategiesByDevice({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query: queryByDevice,
      searchables: apiConfigMaintenanceStrategies.searchables,
      sortables: apiConfigMaintenanceStrategies.sortables,
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export async function getPaginatedMaintenanceStrategies({
  dataTableOptions,
  customFilters,
  customOrFilters,
}: QueryPaginationFilters) {
  const { data, count, error } = await createPaginatedSupabaseQuery({
    baseOptions: {
      table,
      query,
      searchables: apiConfigMaintenanceStrategies.searchables,
      sortables: apiConfigMaintenanceStrategies.sortables,
    },
    dataTableOptions,
    customFilters,
    customOrFilters,
  });
  if (error) throw error;
  return { data, count };
}

export const getMaintenanceStrategiesActive = async (customFilters?: QueryFilter[]) => {
  const q = apiClient.from(table).select(query).eq("is_active", true);

  if (customFilters && customFilters.length > 0) {
    for (const filter of customFilters) {
      q.filter(...filter);
    }
  }

  const { data, error } = await q;
  if (error) throw error;
  return data;
};
